import { action, computed, observable, runInAction } from 'mobx'

import { ModalViewModel } from '@/infra/ModalViewModel'
import { format } from 'date-fns'
import { groupBy, map, sortBy } from 'lodash-es'
import { type TimeRange } from '@/infra/time-range'
import { AsyncPrompt } from '@/infra/AsyncPrompt'
import { type Consultation } from '@/features/jobs/consultations/Consultation'
import { Member } from '@/features/iam/members/Member'
import { type JobType } from '@/features/jobs/job-type'

/**
 * A day and its timeslots for which the pro can book a consultation
 * based on the client's availability.
 */
interface ScheduledDay {
  formattedDay: string
  timeSlots: TimeRange[]
}

/**
 * Information used for creating a consultation and prompting the user.
 */
export interface ConsultationDetails {
  availability: () => TimeRange[]
  bookedConsultations: () => Consultation[]
  requestedLength: () => number
  clientMember: Member
  currentMember: Member
}

/**
 * Consultation details.
 */
const ConsultationDetails: { none: ConsultationDetails } = {
  /**
   * An empty details "instance".
   */
  none: {
    requestedLength: () => 30,
    availability: () => [],
    bookedConsultations: () => [],
    clientMember: Member.none,
    currentMember: Member.none,
  },
}

/**
 * Result of a consultation booking operation.
 */
export type ConsultationPendingBookingResult =
  { type: 'Book'; selectedTimeSlot: TimeRange } | { type: 'Forfeit' }

/**
 * View Model for the Consultation Modal.
 */
export class ConsultationPendingBookingModalViewModel {
  /**
   * The provider's selection.
   */
  private readonly _prompt

  /**
   * The active consultation.
   */
  @observable.ref
  private accessor _activeConsultationDetails: ConsultationDetails =
    ConsultationDetails.none

  @observable
  private accessor _jobType: JobType | undefined = undefined

  readonly modal = new ModalViewModel()

  /**
   * The time slot selected by the pro. This is one of the time slots
   * allowable based on the customer's specified availability.
   */
  @observable
  accessor selectedTimeSlot: TimeRange | null = null

  /**
   * The days and the time slots within them for which the pro can book
   * the consultation based on the client's availability.
   */
  @observable
  accessor availableDays: ScheduledDay[] = []

  constructor() {
    this._prompt = new AsyncPrompt<ConsultationPendingBookingResult>({
      abort$: this.modal.close$,
    })
  }

  /**
   * The customer's requested length in minutes for the Consultation.
   */
  @computed
  get requestedLength() {
    return this._activeConsultationDetails.requestedLength()
  }

  /**
   * The customer's availability for the Consultation.
   */
  @computed
  get availability() {
    return this._activeConsultationDetails.availability()
  }

  /**
   * List of booked consultations.
   */
  @computed
  get bookedConsultations() {
    return this._activeConsultationDetails.bookedConsultations()
  }

  /**
   * The client member.
   */
  @computed
  get clientMember() {
    return this._activeConsultationDetails.clientMember
  }

  /**
   * The current member.
   */
  @computed
  get currentMember() {
    return this._activeConsultationDetails.currentMember
  }

  /**
   * Indicates whether the consultation is for a tax job.
   */
  @computed
  get consultationIsForTaxJob() {
    return this._jobType?.type === 'Tax'
  }

  /**
   * Inflates the Modal.
   */
  async promptForTimeSlots(
    consultationDetails: ConsultationDetails,
    jobType?: JobType,
  ) {
    this.modal.close()

    const responsePromise = this._prompt.prompt()

    runInAction(() => {
      this._activeConsultationDetails = consultationDetails
      this._jobType = jobType
      this.availableDays = this._computeScheduledDays(this.availability)
      this.modal.show()
    })

    const response = await responsePromise
    this.modal.close()
    return response
  }

  /**
   * The booked consultation that overlaps the time slot, if any.
   */
  bookedConsultationAt(range: TimeRange) {
    return this.bookedConsultations.find((c) => c.bookedTime?.overlaps(range))
  }

  /**
   * Whether the time slot shows as selected: picked by the pro, or already
   * taken by another booked consultation.
   */
  isTimeSlotSelected(range: TimeRange) {
    return (
      Boolean(this.selectedTimeSlot?.isEqual(range)) ||
      Boolean(this.bookedConsultationAt(range))
    )
  }

  /**
   * Closes the modal without a selection.
   */
  @action.bound
  close() {
    this.modal.close()
  }

  /**
   * Selects the time slot.
   */
  @action.bound
  selectTimeSlot(range: TimeRange) {
    this.selectedTimeSlot = range
  }

  /**
   * Book the selected time slot.
   */
  @action.bound
  book() {
    if (!this.selectedTimeSlot) {
      return
    }

    this._prompt.respond({
      type: 'Book',
      selectedTimeSlot: this.selectedTimeSlot,
    })
    this.close()
  }

  /**
   * Forfeit the consultation.
   */
  @action.bound
  forfeit() {
    this._prompt.respond({ type: 'Forfeit' })
    this.close()
  }

  /**
   * Groups the customer's availability by day.
   */
  private _computeScheduledDays(ranges: TimeRange[]) {
    const sorted = sortBy(ranges, 'startTime')
    const grouped = groupBy(sorted, (t) => format(t.startTime, 'EEEE, d MMMM'))

    return map(grouped, (timeSlots, formattedDay) => {
      return { formattedDay, timeSlots }
    })
  }
}
